import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { Storefront, MagnifyingGlass, MapPin, CurrencyDollar, Lightning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';

const typeColors = {
  CPA: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  CPL: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  RevShare: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Subscription: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

export default function OfferMarketplace() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchOffers();
  }, [categoryFilter, typeFilter]);

  const fetchOffers = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (typeFilter !== 'all') params.append('offer_type', typeFilter);
      const { data } = await api.get(`/offers/?${params.toString()}`);
      setOffers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyToOffer = async (offerId) => {
    try {
      await api.post(`/offers/${offerId}/apply`);
      toast.success('Applied successfully! Smart link created.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    }
  };

  const filtered = offers.filter(o =>
    o.title?.toLowerCase().includes(search.toLowerCase()) ||
    o.description?.toLowerCase().includes(search.toLowerCase())
  );

  const categories = ['all', 'Technology', 'Finance', 'SaaS', 'Health', 'Education', 'E-Commerce', 'Travel', 'Lifestyle', 'Home'];

  return (
    <div className="space-y-6" data-testid="offer-marketplace">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <Storefront size={32} className="text-blue-400" weight="duotone" />
          Offer Marketplace
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Browse and promote high-converting offers</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="search-offers"
            placeholder="Search offers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder-zinc-500"
          />
        </div>
        <select
          data-testid="filter-category"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-300 text-sm"
        >
          {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
        </select>
        <select
          data-testid="filter-type"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-zinc-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="CPA">CPA</option>
          <option value="CPL">CPL</option>
          <option value="RevShare">RevShare</option>
          <option value="Subscription">Subscription</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((offer, i) => (
            <Card key={offer.offer_id} className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all duration-200 animate-fade-in stagger-${Math.min(i+1, 8)}`} data-testid={`offer-card-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`${typeColors[offer.type] || ''} text-[10px]`}>{offer.type}</Badge>
                  <span className="text-xs text-zinc-500">{offer.offer_id}</span>
                </div>
                <h3 className="font-heading font-semibold text-white text-base mb-1">{offer.title}</h3>
                <p className="text-xs text-zinc-400 mb-4 line-clamp-2">{offer.description}</p>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1.5">
                    <CurrencyDollar size={14} className="text-emerald-400" />
                    <span className="text-sm font-bold text-emerald-400 font-mono">${offer.payout}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-zinc-500" />
                    <span className="text-xs text-zinc-400">{(offer.geo_targets || []).slice(0, 3).join(', ')}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-zinc-500 mb-4">
                  <span>{(offer.total_clicks || 0).toLocaleString()} clicks</span>
                  <span>{offer.conversion_rate || 0}% conv.</span>
                </div>

                {user?.role === 'affiliate' && (
                  <Button
                    data-testid={`apply-offer-${i}`}
                    onClick={() => applyToOffer(offer.offer_id)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm gap-2"
                    size="sm"
                  >
                    <Lightning size={14} /> Apply & Get Link
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-zinc-500">
              <Storefront size={48} className="mx-auto mb-3 opacity-40" />
              <p>No offers found matching your criteria.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
